/*
 * <store-locator> custom element.
 *
 * Drives the search/filter state for the locator section. Cards are server-
 * rendered with `data-*` attributes carrying every filterable value; we just
 * toggle `[hidden]` on each card based on the active predicate.
 *
 * Filter mode (controls which cards are visible):
 *   - "text"   : token-match against data-search-haystack (the Tier A path).
 *   - "radius" : Haversine distance ≤ radius km from a geocoded lat/lng.
 *
 * Location state (independent of filter mode, controls sort + distance labels):
 *   filterState.lat/lng may be set without flipping filter mode to "radius".
 *   Whenever they are set, cards are sorted by Haversine distance ascending
 *   and per-card distance labels are shown. This is the geolocate-button path
 *   (VIS-250): sort by distance from the visitor, without filtering anyone out.
 *
 * On submit / on URL hydration with `q` only, the element fires a fire-and-
 * forget geocode via ./store-locator-geocoder.js. A successful response flips
 * filter mode to "radius"; failure leaves the user in text-match mode.
 *
 * On mount, if the section was paginated by the Liquid `paginate` tag, we
 * fetch the remaining pages via the section-rendering API so client-side
 * filtering covers all stores.
 */

import { geocode } from '@theme/store-locator-geocoder';
import { openStateAt, openOnWeekend } from '@theme/store-hours';
import '@theme/store-locator-geolocate';
import { getFavorite } from '@theme/store-favorites';

const SEARCH_DEBOUNCE_MS = 300;
const GEOCODE_DEBOUNCE_MS = 500;
const GEOCODE_MIN_QUERY_LENGTH = 3;
const COPY_FEEDBACK_MS = 1500;
const OPEN_NOW_REFRESH_MS = 5 * 60 * 1000;
const EARTH_RADIUS_KM = 6371;
// VIS-298: cap concurrent section-render fetches when hydrating the remaining
// pages so first narrowing doesn't fire ~9 requests at once. Same-origin and
// one-time, but smoother on the network.
const HYDRATION_CONCURRENCY = 4;
// VIS-301: when the list follows the map viewport, cap it at the N nearest
// stores in view so a zoomed-out (e.g. country-wide) map doesn't render all
// ~477 cards. The overflow is surfaced as a "zoom in to narrow" note.
const VIEWPORT_LIST_CAP = 50;
// VIS-601: default-state anchor when the visitor hasn't searched or geolocated.
// Mirrors store-locator-map.js — the map frames the whole country while the
// list sorts by distance from this point, so "the nearest stores" still has a
// meaning before any location is known.
const ITALY_CENTER = { lat: 42.5, lng: 12.5 };
// VIS-978: beat between the list scrolling into view and the nudge playing, so the eye
// has landed on the list first.
const FAVORITE_NUDGE_DELAY_MS = 600;

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.filterState = {
      q: '',
      region: '',
      // VIS-155: openings is a Set of 'now' | 'weekend' keys. 'now' is
      // synonymous with the legacy openNow boolean and the ?open=1 URL
      // param (kept on read for back-compat; written as openings=now).
      openings: new Set(),
      // VIS-155 / VIS-796: set of active boolean store flags (Payback, Gift
      // Card) — each drives the matching data-<key> predicate.
      flags: new Set(),
      lat: null,
      lng: null,
      radius: null,
      label: '',
      mode: 'text',
      // Discriminates the source of lat/lng so the map (VIS-252) can render a
      // "you are here" marker only when the visitor geolocated. Geocoded
      // points (e.g. "Milano") set 'geocode'; geolocate sets 'geolocate'.
      // URL hydration can't distinguish; defaults to 'geocode'.
      userLocation: null,
      // VIS-601: how the current lat/lng anchor was set in the default (no
      // search) state — 'geolocate' (visitor position, distance labels shown),
      // 'customer' (VIS-1087: logged-in customer's address, labels suppressed)
      // or 'centroid' (Italy fallback, labels suppressed). Null once a real
      // search takes over. Distinct from userLocation, which the map reads for the
      // "you are here" pin.
      anchorSource: null,
      // VIS-301: when set to {north,south,east,west}, the visible list follows
      // the map viewport instead of the radius. Set by a user pan/zoom on the
      // map, cleared by any other filter change. Never serialized to the URL
      // (transient browse state).
      viewport: null,
    };
    this.searchTimer = null;
    this.geocodeTimer = null;
    this.openNowTimer = null;
    this.geocodeController = null;
    this.geocodeFallbackNoticed = false;
    this.geolocateErrorMessage = '';
    // Multi-page hydration is deferred until the first narrowing (search,
    // region pick, or radius mode). Avoids fetching ~425 extra cards on a
    // page load where the visitor hasn't expressed any intent yet.
    this.hydrationStarted = false;
    // VIS-301: "lat,lng" key of the point the per-card distances were last
    // computed for; lets viewport re-filters skip recomputing them.
    this.distanceCacheKey = null;
    // VIS-978: favorite-toggle nudge, armed once per instance.
    this.favoriteNudgeArmed = false;
    this.favoriteNudgeObserver = null;
    this.favoriteNudgeTimer = null;
    // VIS-1000: the favorite card pinned to the list top, and the sibling it
    // was taken from, so unpinning can put it back.
    this.pinnedCard = null;
    this.pinnedNext = null;
  }

  connectedCallback() {
    this.list = this.querySelector('[data-store-locator-list]');
    this.counter = this.querySelector('[data-store-locator-count]');
    this.searchInput = this.querySelector('[data-store-locator-search-input]');
    this.searchForm = this.querySelector('[data-store-locator-search]');
    this.filterContainer = this.querySelector('[data-store-locator-filters]');
    this.clearButton = this.querySelector('[data-store-locator-clear-filters]');

    // VIS-155 — drawer + bar refs.
    this.drawer = this.querySelector('[data-store-locator-drawer]');
    this.drawerOpenButtons = this.querySelectorAll('[data-store-locator-drawer-open]');
    this.drawerCloseButtons = this.querySelectorAll('[data-store-locator-drawer-close]');
    this.filterBadge = this.querySelector('[data-store-locator-filter-badge]');
    this.radiusSlider = this.querySelector('[data-store-locator-radius-slider]');
    this.radiusValue = this.querySelector('[data-store-locator-radius-value]');
    this.radiusControl = this.querySelector('[data-store-locator-radius-control]');
    this.resultsHeader = this.querySelector('[data-store-locator-results-header]');

    // VIS-283 — mobile list/map view tabs + the deferred map element.
    this.viewTabsEl = this.querySelector('[data-store-locator-view-tabs]');
    this.viewTabs = this.querySelectorAll('[data-view-tab]');
    this.mapElement = this.querySelector('store-locator-map');

    this.activeSearch = this.querySelector('[data-store-locator-active-search]');
    this.radiusChip = this.querySelector('[data-store-locator-radius-chip]');
    this.radiusChipLabel = this.querySelector('[data-store-locator-radius-chip-label]');
    this.radiusChipClear = this.querySelector('[data-store-locator-radius-clear]');
    this.fallbackNotice = this.querySelector('[data-store-locator-fallback-notice]');
    this.radiusEmpty = this.querySelector('[data-store-locator-radius-empty]');
    this.radiusEmptyMessage = this.querySelector('[data-store-locator-radius-empty-message]');
    this.radiusEmptyActions = this.querySelector('[data-store-locator-radius-empty-actions]');
    // VIS-301: "showing the nearest N — zoom in to narrow" note, shown when the
    // viewport-following list is capped at VIEWPORT_LIST_CAP.
    this.viewportOverflow = this.querySelector('[data-store-locator-viewport-overflow]');

    this.locked = this.dataset.locked === 'true';
    // VIS-796: the boolean store flags this locator can filter by, listed by
    // the section (data-flag-keys) rather than hardcoded here. Each key is the
    // chip handle, the `?<key>=1` URL param and the `data-<key>` card
    // attribute, so adding a flag is a Liquid-only change.
    this.flagKeys = (this.dataset.flagKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
    this.radiusEnabled = this.dataset.radiusEnabled === 'true';
    // VIS-298: when the autofill element is present it already resolves an exact
    // place on selection, so the per-keystroke geocode is redundant — we only
    // keep geocode on submit (Enter without picking a suggestion). Detected by
    // the element's presence; it carries the API key when rendered.
    this.autocompleteActive = !!this.querySelector('store-locator-autocomplete');
    this.apiKey = this.dataset.googleMapsApiKey || '';
    this.defaultRadius = parseInt(this.dataset.defaultRadiusKm || '10', 10) || 10;
    this.fallbackSteps = parseFallbackSteps(this.dataset.radiusFallbackSteps);
    this.labelCurrentLocation = this.dataset.labelCurrentLocation || '';

    if (this.radiusEnabled && !this.apiKey) {
      console.warn(
        '[store-locator] Geocoded radius search is enabled on this section but no Google Maps API key is configured. ' +
          'Falling back to text-match search. ' +
          'Set it in Shopify Admin → Settings → Custom data → Shop → "Google Maps API key" to enable.'
      );
    }

    this.#hydrateStateFromUrl();
    // VIS-601: with no URL search, anchor the default list to the visitor's
    // location (only if already granted) or Italy's centroid, so the first paint
    // already shows the nearest stores. Must run before the initial apply.
    this.#initDefaultAnchor();
    this.#applyStateToInputs();
    this.#bindEvents();
    window.addEventListener('store-favorite:changed', this.#onFavoriteChanged);
    this.#refreshOpenStatusForAll();
    this.openNowTimer = setInterval(() => this.#refreshOpenStatusForAll(), OPEN_NOW_REFRESH_MS);
    this.#applyFilters('init');

    // If we landed with `q` only (typical compact-mode redirect), try to
    // geocode it so the user lands in radius mode.
    if (this.radiusEnabled && this.filterState.mode === 'text' && this.filterState.q) {
      this.#scheduleGeocode(0);
    }
  }

  disconnectedCallback() {
    if (this.openNowTimer) clearInterval(this.openNowTimer);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.geocodeTimer) clearTimeout(this.geocodeTimer);
    if (this.geocodeController) this.geocodeController.abort();
    this.#cancelFavoriteNudge();
    window.removeEventListener('store-favorite:changed', this.#onFavoriteChanged);
  }

  // -- Initial state -------------------------------------------------------

  #hydrateStateFromUrl() {
    if (this.locked) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('q')) this.filterState.q = params.get('q').trim();
    if (params.has('region')) this.filterState.region = params.get('region').trim();
    // VIS-155: ?openings=now,weekend is the new shape. ?open=1 is accepted
    // for back-compat with Tier A and the pre-drawer JS.
    if (params.has('openings')) {
      for (const key of params.get('openings').split(',').map((s) => s.trim()).filter(Boolean)) {
        if (key === 'now' || key === 'weekend') this.filterState.openings.add(key);
      }
    }
    if (params.has('open') && params.get('open') === '1') {
      this.filterState.openings.add('now');
    }
    for (const key of this.flagKeys) {
      if (params.get(key) === '1') this.filterState.flags.add(key);
    }

    if (this.radiusEnabled) {
      const lat = parseCoord(params.get('lat'));
      const lng = parseCoord(params.get('lng'));
      if (lat != null && lng != null) {
        this.filterState.lat = lat;
        this.filterState.lng = lng;
        this.filterState.radius = parseRadius(params.get('radius')) || this.defaultRadius;
        // When geolocate set the URL there's no `q` — fall back to the
        // "your location" label so the chip reads naturally.
        this.filterState.label = this.filterState.q || this.labelCurrentLocation;
        this.filterState.mode = 'radius';
        // If the URL has lat/lng but no q, the previous session must have
        // geolocated (typed-city searches always pass through the search
        // input, which writes q). Restore 'geolocate' so the chip uses the
        // current-location label template and the map drops a "you are
        // here" pin. With q present the URL is from a geocoded search.
        this.filterState.userLocation = this.filterState.q ? 'geocode' : 'geolocate';
      }
    }
  }

  // VIS-601: the locator is active by default and the list is sorted by distance
  // from an anchor. Seed the anchor with Italy's centroid immediately so the
  // first paint is anchored, then upgrade to the visitor's position only if
  // geolocation was already granted (no prompt on a cold visit — the manual
  // "Use my location" button stays the explicit opt-in for everyone else).
  #initDefaultAnchor() {
    if (this.locked) return;
    // A query, region, or URL-hydrated location already gives us a state — don't
    // override it.
    if (this.filterState.q || this.filterState.region) return;
    if (Number.isFinite(this.filterState.lat) && Number.isFinite(this.filterState.lng)) return;

    this.filterState.lat = ITALY_CENTER.lat;
    this.filterState.lng = ITALY_CENTER.lng;
    this.filterState.anchorSource = 'centroid';
    this.filterState.userLocation = null;

    if (this.radiusEnabled) {
      this.#useCustomerLocation();
      this.#useLocationIfGranted();
    }
  }

  // VIS-1087: a logged-in customer with an Italian default address gets the
  // list sorted from their ZIP instead of Italy's centroid. Granted geolocation
  // still wins, whichever resolves first.
  async #useCustomerLocation() {
    const query = this.dataset.customerLocation;
    if (!query || !this.apiKey) return;
    const result = await geocode(query, { apiKey: this.apiKey });
    if (!result || !this.#isOnDefaultAnchor(['centroid'])) return;
    this.filterState.lat = result.lat;
    this.filterState.lng = result.lng;
    this.filterState.anchorSource = 'customer';
    this.#applyFilters('anchor');
  }

  // Still on an automatic anchor from `sources`: no search, region, map pan
  // or manual geolocate has happened since it was seeded. Those leave
  // anchorSource untouched, so it can't be the only check.
  #isOnDefaultAnchor(sources) {
    const s = this.filterState;
    return sources.includes(s.anchorSource) && !s.viewport && this.#isDefaultActive();
  }

  async #useLocationIfGranted() {
    if (!navigator.geolocation || !navigator.permissions || !navigator.permissions.query) return;
    let status;
    try {
      status = await navigator.permissions.query({ name: 'geolocation' });
    } catch {
      return; // Geolocation isn't queryable here (older Safari) — stay on centroid.
    }
    if (status.state !== 'granted') return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords || {};
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
        // Bail if the visitor searched between the query and the callback.
        if (!this.#isOnDefaultAnchor(['centroid', 'customer'])) return;
        this.filterState.lat = latitude;
        this.filterState.lng = longitude;
        this.filterState.anchorSource = 'geolocate';
        this.filterState.userLocation = 'geolocate';
        this.#applyFilters('anchor');
      },
      () => {}, // permission revoked between query and call — stay on centroid
      { maximumAge: 5 * 60 * 1000, timeout: 10000 }
    );
  }

  #applyStateToInputs() {
    if (this.searchInput && this.filterState.q) {
      this.searchInput.value = this.filterState.q;
    }

    this.querySelectorAll('[data-store-filter]').forEach((input) => {
      const key = input.dataset.filterKey;
      if (key === 'region' && input.tagName === 'SELECT') {
        input.value = this.filterState.region;
      }
    });

    // VIS-155: openings checkboxes + chips share state.
    this.querySelectorAll('[data-store-locator-opening]').forEach((checkbox) => {
      checkbox.checked = this.filterState.openings.has(checkbox.dataset.openingKey);
    });

    this.querySelectorAll('[data-store-locator-chip]').forEach((chip) => {
      const handle = chip.dataset.chipHandle;
      const kind = chip.dataset.chipKind;
      let active = false;
      if (kind === 'opening') active = this.filterState.openings.has(handle);
      else if (kind === 'flag') active = this.filterState.flags.has(handle);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this.radiusSlider && this.filterState.radius) {
      this.radiusSlider.value = String(this.filterState.radius);
    }
    this.#updateRadiusUi();
  }

  // The filter drawer + chip toggles only narrow meaningfully when a
  // location is known (geocoded city or geolocate). Until then, disable
  // them so the visitor isn't invited to toggle filters that can't show
  // results.
  #updateLocationGatedUi() {
    const locationActive = this.filterState.mode === 'radius';
    this.drawerOpenButtons.forEach((btn) => {
      btn.disabled = !locationActive;
    });
    this.querySelectorAll('[data-store-locator-chip]').forEach((chip) => {
      chip.disabled = !locationActive;
    });
  }

  #bindEvents() {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        // VIS-283: a new location search is the visitor signalling intent —
        // swing back to Lista negozi so they see the resulting count + any
        // expand-radius actions before they reach for the Mappa tab.
        this.setViewMode('list');
        this.#scheduleApply(0, 'search-settled');
        if (this.radiusEnabled && this.filterState.q) {
          this.#scheduleGeocode(0);
        }
      });
    }
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (event) => {
        const value = event.target.value.trim();
        this.filterState.q = value;
        // Any edit drops radius mode immediately so the user sees text-match
        // feedback while they type. A successful geocode will flip back.
        if (this.filterState.mode === 'radius') {
          this.filterState.mode = 'text';
          this.filterState.lat = null;
          this.filterState.lng = null;
          this.filterState.radius = null;
          this.filterState.label = '';
          this.filterState.userLocation = null;
          // VIS-283: editing after a radius search starts a new location
          // search — swing back to Lista negozi for the same reason as the
          // submit + geolocate paths.
          this.setViewMode('list');
        }
        this.geocodeFallbackNoticed = false;
        this.geolocateErrorMessage = '';
        this.#scheduleApply(SEARCH_DEBOUNCE_MS, 'search-keystroke');
        // VIS-298: skip the per-keystroke geocode when autofill is active — the
        // suggestion the visitor picks geocodes exactly via place details, and a
        // bare Enter still geocodes through the submit handler below.
        if (this.radiusEnabled && value && !this.autocompleteActive) {
          this.#scheduleGeocode(GEOCODE_DEBOUNCE_MS);
        }
      });
    }
    // Region select (lives in the drawer now, but the existing
    // [data-store-filter] hook is still the cleanest selector).
    this.addEventListener('change', (event) => {
      const filter = event.target.closest('[data-store-filter]');
      if (filter) {
        this.#updateFilterFromInput(filter);
        this.#scheduleApply(0, 'filter');
        return;
      }
      const opening = event.target.closest('[data-store-locator-opening]');
      if (opening) {
        this.#toggleOpening(opening.dataset.openingKey, opening.checked);
        this.#scheduleApply(0, 'filter');
      }
    });
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.#clearFilters();
        this.#scheduleApply(0, 'filter');
      });
    }
    // VIS-155: any [data-store-locator-clear-filters] element (the drawer
    // footer's CANCELLA FILTRI button) clears too.
    this.querySelectorAll('[data-store-locator-clear-filters]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.#clearFilters();
        this.#scheduleApply(0, 'filter');
      });
    });
    // VIS-155: chip strip — single delegated listener, works for both the
    // bar and the drawer's chip strip.
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-store-locator-chip]');
      if (!chip) return;
      event.preventDefault();
      const handle = chip.dataset.chipHandle;
      const kind = chip.dataset.chipKind;
      const isActive = chip.getAttribute('aria-pressed') === 'true';
      if (kind === 'opening') this.#toggleOpening(handle, !isActive);
      else if (kind === 'flag') this.#toggleFlag(handle, !isActive);
      this.#scheduleApply(0, 'filter');
    });
    // VIS-155: drawer open / close + radius slider.
    if (this.drawerOpenButtons) {
      this.drawerOpenButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.#openDrawer());
      });
    }
    if (this.drawerCloseButtons) {
      this.drawerCloseButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.#closeDrawer());
      });
    }
    if (this.drawer) {
      // Backdrop click + Escape closing — native <dialog> emits a cancel
      // event for Escape and a click event for the backdrop (the click
      // target is the dialog element itself when the backdrop is hit).
      this.drawer.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.#closeDrawer();
      });
      this.drawer.addEventListener('click', (event) => {
        if (event.target === this.drawer) this.#closeDrawer();
      });
    }
    if (this.radiusSlider) {
      this.radiusSlider.addEventListener('input', () => {
        const next = parseInt(this.radiusSlider.value, 10);
        if (!Number.isFinite(next) || next <= 0) return;
        this.filterState.radius = next;
        this.#updateRadiusUi();
        this.#scheduleApply(0, 'filter');
      });
    }
    if (this.radiusChipClear) {
      this.radiusChipClear.addEventListener('click', () => {
        if (this.filterState.mode === 'radius') {
          // Clear the whole radius search → back to the default anchored state.
          this.#clearRadius();
          this.filterState.q = '';
          if (this.searchInput) this.searchInput.value = '';
          this.#initDefaultAnchor();
          this.#scheduleApply(0, 'filter');
        } else {
          // VIS-601: default anchored state with the map panned — drop only the
          // viewport narrowing (keep the centroid/visitor anchor) and re-frame
          // the map back to the whole country so map + list realign.
          this.filterState.viewport = null;
          this.#scheduleApply(0, 'filter');
          if (this.mapElement && typeof this.mapElement.recenterDefault === 'function') {
            this.mapElement.recenterDefault();
          }
        }
      });
    }
    if (this.radiusEmptyActions) {
      this.radiusEmptyActions.addEventListener('click', (event) => {
        const expandButton = event.target.closest('[data-radius-expand]');
        if (expandButton) {
          const next = parseInt(expandButton.dataset.radiusExpand, 10);
          if (!Number.isFinite(next) || next <= 0) return;
          this.filterState.radius = next;
          this.#scheduleApply(0, 'filter');
          return;
        }
        const clearOthers = event.target.closest('[data-radius-clear-others]');
        if (clearOthers) {
          this.filterState.region = '';
          this.filterState.openings.clear();
          this.filterState.flags.clear();
          this.#applyStateToInputs();
          this.#scheduleApply(0, 'filter');
        }
      });
    }
    // VIS-283: mobile list/map view tabs.
    if (this.viewTabs && this.viewTabs.length) {
      this.viewTabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setViewMode(tab.dataset.viewTab));
      });
    }
    this.addEventListener('click', this.#handleDelegatedClick);
    this.addEventListener('store-locator:geolocated', this.#handleGeolocated);
    this.addEventListener('store-locator:geolocate-error', this.#handleGeolocateError);
    // VIS-298: autofill selection from <store-locator-autocomplete>.
    this.addEventListener('store-locator:place-selected', this.#handlePlaceSelected);
  }

  // VIS-283: toggle the mobile list/map view. Desktop renders both columns
  // regardless of this attribute (the CSS only reacts below the map
  // breakpoint). The first switch to "map" activates the deferred map element
  // so its Maps bundle loads lazily. Public — the map element calls this on
  // marker click to swap back to the cards list.
  setViewMode(mode) {
    if (mode !== 'list' && mode !== 'map') return;
    this.dataset.viewMode = mode;
    this.viewTabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.viewTab === mode ? 'true' : 'false');
    });
    if (mode === 'map' && this.mapElement) {
      if (typeof this.mapElement.activate === 'function') this.mapElement.activate();
      // The map may have skipped fitBounds while it was hidden — let it
      // relayout and replay any deferred fit now that it has a real size.
      if (typeof this.mapElement.onBecameVisible === 'function') {
        this.mapElement.onBecameVisible();
      }
    }
  }

  // VIS-301: called by <store-locator-map> whenever the map settles (initial
  // fit, recenters, or a user pan/zoom) with the current map bounds. Switches
  // the visible list to viewport-following so the cards always match the markers
  // on the map. Needs a geo anchor for the distance sort — a radius search or
  // the default centroid/visitor anchor (VIS-601); a bare text search has none
  // and ignores map moves. Re-running #applyFilters('viewport') keeps the
  // viewport — every other reason clears it back to the anchored framing.
  syncListToViewport(bounds) {
    if (!bounds) return;
    // Viewport-follow needs a geographic anchor to sort + bound by: a radius
    // search, or the default centroid/visitor anchor (VIS-601). A bare text
    // search has none, so it ignores map moves.
    const hasAnchor =
      Number.isFinite(this.filterState.lat) && Number.isFinite(this.filterState.lng);
    // Radius search follows the viewport regardless; the default anchored state
    // follows it too, but a text search (q present) does not — its results are
    // token matches, not a geographic area.
    if (this.filterState.mode !== 'radius' && (!hasAnchor || this.filterState.q)) return;
    this.filterState.viewport = bounds;
    this.#applyFilters('viewport');
  }

  #updateFilterFromInput(input) {
    const key = input.dataset.filterKey;
    if (key === 'region' && input.tagName === 'SELECT') {
      this.filterState.region = input.value.trim();
    }
  }

  #toggleOpening(key, on) {
    if (!key) return;
    if (on) this.filterState.openings.add(key);
    else this.filterState.openings.delete(key);
    this.#applyStateToInputs();
  }

  #toggleFlag(key, on) {
    if (on) this.filterState.flags.add(key);
    else this.filterState.flags.delete(key);
    this.#applyStateToInputs();
  }

  #clearFilters() {
    this.filterState.q = '';
    this.filterState.region = '';
    this.filterState.openings.clear();
    this.filterState.flags.clear();
    this.#clearRadius();
    if (this.searchInput) this.searchInput.value = '';
    this.querySelectorAll('[data-store-filter]').forEach((input) => {
      if (input.tagName === 'SELECT') input.value = '';
    });
    this.querySelectorAll('[data-store-locator-opening]').forEach((c) => {
      c.checked = false;
    });
    this.querySelectorAll('[data-store-locator-chip]').forEach((c) => {
      c.setAttribute('aria-pressed', 'false');
    });
    // VIS-601: clearing everything returns to the default anchored state rather
    // than a blank list.
    this.#initDefaultAnchor();
  }

  #openDrawer() {
    if (!this.drawer) return;
    this.drawer.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.drawerOpenButtons.forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }

  #closeDrawer() {
    if (!this.drawer || !this.drawer.open) return;
    this.drawer.classList.add('store-locator__drawer--closing');
    const finish = () => {
      this.drawer.classList.remove('store-locator__drawer--closing');
      this.drawer.close();
      document.documentElement.removeAttribute('scroll-lock');
      this.drawerOpenButtons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    };
    // If reduced-motion is on the slide-out transition is suppressed by CSS,
    // so transitionend may never fire. Fall back to a short timeout matched
    // to the CSS close duration (140ms) plus a small safety margin.
    const fallback = setTimeout(finish, 200);
    this.drawer.addEventListener(
      'transitionend',
      () => {
        clearTimeout(fallback);
        finish();
      },
      { once: true }
    );
  }

  #updateRadiusUi() {
    if (!this.radiusSlider) return;
    // Source of truth is filterState — the slider DOM mirrors it. Without
    // this, the expand-to-N buttons and the URL-hydrated radius silently
    // diverge from the drawer's displayed value.
    const inRadiusMode = this.filterState.mode === 'radius';
    const value = this.filterState.radius || this.defaultRadius || parseInt(this.radiusSlider.value, 10) || 0;
    this.radiusSlider.value = String(value);
    if (this.radiusValue) this.radiusValue.textContent = `${value} km`;
    if (this.radiusControl) {
      this.radiusControl.setAttribute('data-disabled', inRadiusMode ? 'false' : 'true');
      this.radiusSlider.disabled = !inRadiusMode;
    }
  }

  #updateFilterBadge() {
    if (!this.filterBadge) return;
    // Badge dot flags drawer-only state. Bar chips already show selected
    // state inline, so they don't count.
    const active =
      this.filterState.region !== '' ||
      this.filterState.openings.has('weekend') ||
      (this.filterState.mode === 'radius' && this.filterState.radius && this.filterState.radius !== this.defaultRadius);
    this.filterBadge.hidden = !active;
  }

  #clearRadius() {
    this.filterState.lat = null;
    this.filterState.lng = null;
    this.filterState.radius = null;
    this.filterState.label = '';
    this.filterState.mode = 'text';
    this.filterState.userLocation = null;
    this.geocodeFallbackNoticed = false;
    if (this.geocodeController) {
      this.geocodeController.abort();
      this.geocodeController = null;
    }
  }

  // -- Predicate / apply ---------------------------------------------------

  #scheduleApply(delay, reason = 'filter') {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (delay <= 0) {
      this.#applyFilters(reason);
      return;
    }
    this.searchTimer = setTimeout(() => this.#applyFilters(reason), delay);
  }

  #applyFilters(reason = 'filter') {
    // VIS-301: any filter/location change re-frames the map, so it clears the
    // viewport-follow state — only a map-driven settle ('viewport'), the
    // background page hydration ('hydrate') or a favorite unpin ('unpin') keep
    // the visitor's current view.
    if (reason !== 'viewport' && reason !== 'hydrate' && reason !== 'unpin') {
      this.filterState.viewport = null;
    }
    this.#unpinFavorite();
    const cards = this.list ? this.list.querySelectorAll('[data-store-card]') : [];

    // VIS-601: the locator is active by default — there's no idle state. With no
    // query the predicate matches every card; the anchored distance sort and the
    // nearest-N cap below narrow the visible set, so the default view is "the
    // closest stores" rather than all ~480.
    const predicate = this.#buildPredicate();
    const useDistance = this.#useDistanceSort();
    // `matched` is the full count of stores passing the predicate. The rendered
    // list is then capped to the nearest N, but the counter reports `matched` so
    // the landing conveys the whole network ("480 stores") even though only ~50
    // cards are shown (VIS-601).
    let matched = 0;
    cards.forEach((card) => {
      const match = predicate(card);
      card.hidden = !match;
      if (match) matched += 1;
    });
    if (useDistance) {
      this.#applyDistanceSort();
    }
    // Cap the rendered list at the nearest N whenever it follows the map viewport
    // (VIS-301) or sits in the default anchored state (VIS-601) — a country-wide
    // view would otherwise render every store. The distance sort above ordered
    // the visible cards nearest-first, so anything past the cap is hidden and
    // surfaced as a "zoom in to narrow" note.
    let overflow = 0;
    if (this.filterState.viewport || this.#isDefaultActive()) {
      overflow = this.#applyViewportCap();
    }
    this.#renderViewportOverflow(overflow);
    // VIS-1000: an 'unpin' pass only drops the old pin; a newly picked favorite
    // waits for the next apply so the card doesn't jump away from the click.
    if (reason !== 'unpin') this.#pinFavorite(predicate);
    this.#updateDistanceLabels(this.#showDistanceLabels());
    this.#updateCounter(matched);
    this.#updateClearButtonVisibility();
    this.#updateRadiusUi();
    this.#updateLocationGatedUi();
    this.#syncStateToUrl();
    this.#renderActiveSearch(matched);
    this.#emitStateChanged(matched, reason);

    // Hydrate the remaining pages once, on the first apply, so the full store
    // set is available for a correct nearest-N + viewport-follow (VIS-601: this
    // used to wait for the first narrowing).
    if (!this.hydrationStarted) {
      this.hydrationStarted = true;
      // VIS-978: arm the nudge only once hydration has settled. Its 'hydrate'
      // re-sort otherwise lands mid-animation and moves the nudged card out
      // of the top slot (it merges the nearer stores from pages 2+).
      void this.#hydrateRemainingPages().finally(() => this.#armFavoriteNudge());
    }
  }

  // VIS-1000: the favorite store always heads the list, regardless of distance:
  // it bypasses the nearest-N cap and the radius/viewport cutoffs, but still
  // honors the text search, region and attribute filters. A card pulled in from
  // outside the results is flagged data-store-pinned so the map leaves it out
  // of its framing.
  #pinFavorite(predicate) {
    const favorite = getFavorite();
    if (!this.list || !favorite) return;
    const card = this.list.querySelector(`[data-store-card][data-handle="${CSS.escape(favorite.handle)}"]`);
    if (!(card instanceof HTMLElement) || !predicate(card, true)) return;
    card.toggleAttribute('data-store-pinned', card.hidden);
    card.hidden = false;
    this.pinnedCard = card;
    this.pinnedNext = card.nextElementSibling;
    this.list.prepend(card);
  }

  // Runs first on every apply: puts the pinned card back where it came from,
  // so the list is in its natural order again. Without it, text-match results
  // (which keep document order, no re-sort) would keep an unpinned card on top.
  #unpinFavorite() {
    const card = this.pinnedCard;
    if (!card) return;
    card.removeAttribute('data-store-pinned');
    if (this.list) this.list.insertBefore(card, this.pinnedNext);
    this.pinnedCard = null;
    this.pinnedNext = null;
  }

  // Clearing (or replacing) the pinned favorite re-renders the list without it.
  #onFavoriteChanged = () => {
    const favorite = getFavorite();
    if (this.pinnedCard && this.pinnedCard.dataset.handle !== (favorite && favorite.handle)) {
      this.#applyFilters('unpin');
    }
  };

  // Distance sort applies whenever there's a geographic anchor and we're not
  // running a text-token search: a radius search, geolocate, or the default
  // centroid/visitor anchor (VIS-601). Text-match results keep document order.
  #useDistanceSort() {
    const { q, lat, lng, mode } = this.filterState;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (mode === 'radius') return true;
    return !q;
  }

  // The default landing state: unlocked, no query/region, not in radius mode —
  // "all stores, nearest first", capped so we never list all ~480.
  #isDefaultActive() {
    const s = this.filterState;
    return !this.locked && !s.q && !s.region && s.mode !== 'radius';
  }

  // Per-card "N km" labels only make sense against the visitor's real position
  // (geolocate or a geocoded search), not the Italy-centroid fallback (VIS-601).
  #showDistanceLabels() {
    if (!this.#useDistanceSort()) return false;
    return this.filterState.mode === 'radius' || this.filterState.anchorSource === 'geolocate';
  }

  // -- VIS-978 favorite-toggle nudge ----------------------------------------
  // Visitors with no favorite get one "thrill" of the first visible card's
  // toggle each time a locator mounts (page view or popup), the first time its
  // list scrolls into view. There is deliberately no seen flag: the nudge keeps
  // coming back until a favorite is picked, which retires it. Armed once the
  // page hydration has settled; the target is still resolved when it plays, so
  // a late 'anchor' re-sort lands it on the new nearest store.

  #armFavoriteNudge() {
    if (this.favoriteNudgeArmed || !this.isConnected) return;
    this.favoriteNudgeArmed = true;
    if (!this.list || getFavorite() != null) return;
    // The CSS guard alone would already keep it still; skipping here also
    // spares the observer and timer.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    window.addEventListener('store-favorite:changed', this.#cancelFavoriteNudge);
    // A hidden list (mobile map tab, closed popup) never intersects, so the
    // nudge waits until the visitor actually looks at the cards.
    this.favoriteNudgeObserver = new IntersectionObserver(this.#onFavoriteNudgeIntersect, {
      rootMargin: '0px 0px -20% 0px',
    });
    this.favoriteNudgeObserver.observe(this.list);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onFavoriteNudgeIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (this.favoriteNudgeObserver) this.favoriteNudgeObserver.disconnect();
    this.favoriteNudgeObserver = null;
    this.favoriteNudgeTimer = setTimeout(this.#playFavoriteNudge, FAVORITE_NUDGE_DELAY_MS);
  };

  #playFavoriteNudge = () => {
    this.favoriteNudgeTimer = null;
    window.removeEventListener('store-favorite:changed', this.#cancelFavoriteNudge);
    if (getFavorite() != null || !this.list) return;
    const toggle = this.list.querySelector('[data-store-card]:not([hidden]) .store-card__favorite-toggle');
    if (!(toggle instanceof HTMLElement)) return;
    if (typeof toggle.checkVisibility === 'function' && !toggle.checkVisibility()) return;
    toggle.classList.add('store-card__favorite-toggle--nudge');
    // The knob's animationend bubbles up first; wait for the longer ring.
    const onEnd = (event) => {
      if (event.animationName !== 'store-favorite-nudge-ring') return;
      toggle.classList.remove('store-card__favorite-toggle--nudge');
      toggle.removeEventListener('animationend', onEnd);
    };
    toggle.addEventListener('animationend', onEnd);
  };

  #cancelFavoriteNudge = () => {
    if (this.favoriteNudgeObserver) this.favoriteNudgeObserver.disconnect();
    this.favoriteNudgeObserver = null;
    if (this.favoriteNudgeTimer) clearTimeout(this.favoriteNudgeTimer);
    this.favoriteNudgeTimer = null;
    window.removeEventListener('store-favorite:changed', this.#cancelFavoriteNudge);
  };

  #emitStateChanged(visibleCount, reason) {
    this.dispatchEvent(
      new CustomEvent('store-locator:state-changed', {
        bubbles: true,
        detail: {
          visibleCount,
          reason,
          filterState: { ...this.filterState },
        },
      })
    );
  }

  #buildPredicate() {
    const { q, region, openings, flags, lat, lng, radius, mode, viewport } = this.filterState;
    const tokens = mode === 'text' && q ? q.toLowerCase().split(/\s+/).filter(Boolean) : [];
    // VIS-601: distance ordering applies to radius searches AND the default
    // anchored state (centroid/visitor), but not while the visitor is typing a
    // text query — then we token-match instead.
    const hasAnchor = Number.isFinite(lat) && Number.isFinite(lng);
    const useDistance = hasAnchor && (mode === 'radius' || !q);
    // VIS-301: per-card distance depends only on the anchor point, which doesn't
    // change while the visitor pans/zooms. Recompute it only when the point
    // actually changed (new search/geolocate); viewport re-filters reuse the
    // cached value, saving ~477 haversines on every map move.
    const locationKey = useDistance ? `${lat},${lng}` : '';
    const recomputeDistance = locationKey !== this.distanceCacheKey;
    this.distanceCacheKey = locationKey;

    // ignoreDistance skips the radius/viewport cutoffs (VIS-1000 favorite pin).
    return (card, ignoreDistance = false) => {
      if (region && card.dataset.region !== region.toLowerCase()) return false;

      if (useDistance) {
        const cLat = parseCoord(card.dataset.lat);
        const cLng = parseCoord(card.dataset.lng);
        if (cLat == null || cLng == null) return false;
        // Distance from the anchor drives the label + sort, even when the visible
        // set is bounded by the map viewport rather than the radius (VIS-301: the
        // list follows the map once it has been framed).
        if (recomputeDistance || card.dataset.distanceKm === undefined) {
          card.dataset.distanceKm = haversine(lat, lng, cLat, cLng).toFixed(1);
        }
        // VIS-301: always compare the rounded cached value, so a borderline store
        // (10.04 km on a 10 km radius) gets the same answer on every apply and
        // matches its "10.0 km" label.
        const distance = parseFloat(card.dataset.distanceKm);
        // Radius mode applies a hard cutoff; the default anchored state has none
        // (it's the nearest N), optionally bounded by the map viewport.
        if (!ignoreDistance) {
          if (viewport) {
            if (!withinBounds(cLat, cLng, viewport)) return false;
          } else if (mode === 'radius' && distance > radius) {
            return false;
          }
        }
      } else if (tokens.length > 0) {
        const haystack = card.dataset.searchHaystack || '';
        for (const token of tokens) {
          if (!haystack.includes(token)) return false;
        }
      }

      if (openings.has('now')) {
        const status = card.querySelector('[data-store-open-status]');
        if (!status || status.dataset.openState !== 'open') return false;
      }
      if (openings.has('weekend')) {
        if (!cardOpenWeekend(card)) return false;
      }

      // getAttribute, not dataset: the flag key is the attribute name verbatim,
      // so a hyphenated key can't silently miss its camelCased dataset entry.
      for (const key of flags) {
        if (card.getAttribute(`data-${key}`) !== 'true') return false;
      }

      return true;
    };
  }

  #applyDistanceSort() {
    if (!this.list) return;
    const visible = Array.from(this.list.querySelectorAll('[data-store-card]:not([hidden])'));
    visible.sort(
      (a, b) => parseFloat(a.dataset.distanceKm || '0') - parseFloat(b.dataset.distanceKm || '0')
    );
    const fragment = document.createDocumentFragment();
    visible.forEach((card) => fragment.appendChild(card));
    this.list.appendChild(fragment);
  }

  // VIS-301: hide the in-viewport cards past the nearest VIEWPORT_LIST_CAP and
  // return how many were dropped. Runs only in viewport-follow mode, after the
  // distance sort, so the cards left visible are the closest N to the search
  // point. Returns 0 when the view holds at most the cap.
  #applyViewportCap() {
    if (!this.list) return 0;
    const visible = Array.from(this.list.querySelectorAll('[data-store-card]:not([hidden])'));
    const overflow = visible.length - VIEWPORT_LIST_CAP;
    if (overflow <= 0) return 0;
    for (let i = VIEWPORT_LIST_CAP; i < visible.length; i += 1) {
      visible[i].hidden = true;
    }
    return overflow;
  }

  #renderViewportOverflow(overflow) {
    if (!this.viewportOverflow) return;
    if (overflow > 0) {
      this.viewportOverflow.hidden = false;
      const template = this.dataset.labelViewportOverflowHtml || '';
      this.viewportOverflow.innerHTML = this.#fillTemplate(template, { count: overflow });
    } else {
      this.viewportOverflow.hidden = true;
      this.viewportOverflow.textContent = '';
    }
  }

  #updateDistanceLabels(show) {
    const template = this.dataset.labelDistanceHtml || '';
    this.querySelectorAll('[data-store-distance]').forEach((el) => {
      const card = el.closest('[data-store-card]');
      if (!card) return;
      if (!show || card.hidden || !card.dataset.distanceKm) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      el.hidden = false;
      el.innerHTML = template
        .replace('{{ distance }}', escapeHtml(card.dataset.distanceKm))
        .replace('{distance}', escapeHtml(card.dataset.distanceKm));
    });
  }

  #updateCounter(count) {
    if (!this.counter) return;
    // The results header + mobile list/map tabs are always shown now that the
    // locator is active by default (VIS-601 removed the idle state).
    if (this.resultsHeader) this.resultsHeader.hidden = false;
    // VIS-283: a store list now exists — reveal the mobile list/map tabs.
    if (this.viewTabsEl) this.viewTabsEl.hidden = false;
    // VIS-932: the section hides the running total on the network-wide locator.
    // The element stays in the DOM (it gates the header reveal above) and still
    // carries the empty state, which is not a total.
    if (this.counter.dataset.hideTotal !== undefined) {
      this.counter.hidden = count !== 0;
      this.counter.textContent = count === 0 ? this.counter.dataset.emptyLabel || '' : '';
      return;
    }
    if (count === 0) {
      this.counter.textContent = this.counter.dataset.emptyLabel || '';
      return;
    }
    const template =
      count === 1
        ? this.counter.dataset.countSingularTemplate
        : this.counter.dataset.countPluralTemplate;
    if (template) {
      this.counter.textContent = template.replace('{count}', String(count));
    } else {
      this.counter.textContent = String(count);
    }
  }

  #updateClearButtonVisibility() {
    const hasFilters =
      this.filterState.q !== '' ||
      this.filterState.region !== '' ||
      this.filterState.openings.size > 0 ||
      this.filterState.flags.size > 0 ||
      this.filterState.mode === 'radius';
    if (this.clearButton) this.clearButton.hidden = !hasFilters;
    this.#updateFilterBadge();
  }

  #syncStateToUrl() {
    if (this.locked) return;
    if (typeof window === 'undefined' || !window.history) return;

    const params = new URLSearchParams(window.location.search);
    const setOrDelete = (key, value) => {
      if (value === '' || value == null || value === false) params.delete(key);
      else params.set(key, value);
    };
    setOrDelete('q', this.filterState.q);
    setOrDelete('region', this.filterState.region);
    // VIS-155: drop the legacy `open=1` param now that the new shape is in
    // place. We still accept it on read for back-compat with old links.
    params.delete('open');
    if (this.filterState.openings.size > 0) {
      params.set('openings', Array.from(this.filterState.openings).sort().join(','));
    } else {
      params.delete('openings');
    }
    for (const key of this.flagKeys) {
      setOrDelete(key, this.filterState.flags.has(key) ? '1' : '');
    }
    if (this.filterState.mode === 'radius') {
      setOrDelete('lat', this.filterState.lat);
      setOrDelete('lng', this.filterState.lng);
      setOrDelete('radius', this.filterState.radius);
    } else {
      params.delete('lat');
      params.delete('lng');
      params.delete('radius');
    }

    const search = params.toString();
    const url = window.location.pathname + (search ? '?' + search : '') + window.location.hash;
    window.history.replaceState(null, '', url);
  }

  // -- Active search rendering --------------------------------------------

  #renderActiveSearch(visibleCount) {
    if (!this.activeSearch) return;
    const mode = this.filterState.mode;
    const viewportActive = !!this.filterState.viewport;
    const isDefault = this.#isDefaultActive();
    // VIS-601: the location chip is always present in the default anchored state
    // (not only in radius mode), as the cue for what the list is scoped to.
    const showChip = mode === 'radius' || viewportActive || isDefault;
    // Whole-country view = the current map area still holds every store (the
    // landing, or fully zoomed out). Once the visitor zooms into an area, fewer
    // stores match than are loaded, and the chip flips to "in this area".
    const totalCards = this.list
      ? this.list.querySelectorAll('[data-store-card]').length
      : 0;
    const wholeCountry = !viewportActive || (totalCards > 0 && visibleCount >= totalCards);
    const showGeocodeFallback =
      mode === 'text' && this.geocodeFallbackNoticed && this.filterState.q !== '';
    const showGeolocateError = this.geolocateErrorMessage !== '';
    const showFallback = showGeocodeFallback || showGeolocateError;
    // VIS-301: in viewport-follow mode an empty list means "no store in this
    // map area" — the radius "expand to N km" affordance doesn't apply (the
    // lever is zoom), so suppress it and let the counter show the 0 state.
    const showEmpty = mode === 'radius' && !viewportActive && visibleCount === 0;

    // VIS-155 moved the chip out of the active-search container (now lives
    // next to the count). The container only gates on fallback / empty.
    this.activeSearch.hidden = !(showFallback || showEmpty);

    if (this.radiusChip) {
      this.radiusChip.hidden = !showChip;
      // The "in tutta Italia" default status isn't a filter the visitor applied,
      // so it carries no clear button; the radius and "in questa zona" chips do.
      let removable = true;
      if (showChip && this.radiusChipLabel) {
        if (mode === 'radius' && !viewportActive) {
          // Pick the geolocate-specific template when the user's location is
          // the source — Italian needs the "dalla" contraction baked in, not
          // "da {{ label }}" with label="la tua posizione".
          const template =
            this.filterState.userLocation === 'geolocate' && this.dataset.labelActiveSearchGeolocateHtml
              ? this.dataset.labelActiveSearchGeolocateHtml
              : this.dataset.labelActiveSearchHtml;
          this.radiusChipLabel.innerHTML = this.#fillTemplate(template, {
            radius: this.filterState.radius,
            label: this.#displayLabel(),
          });
        } else if (isDefault && wholeCountry) {
          // VIS-601: default landing shows the whole network, not a framed area.
          this.radiusChipLabel.innerHTML = this.dataset.labelActiveSearchCountryHtml || '';
          removable = false;
        } else {
          // The visitor has framed the map to an area (zoomed/panned), so a
          // "within N km" chip would be stale — label it "in this area" instead.
          this.radiusChipLabel.innerHTML = this.dataset.labelActiveSearchAreaHtml || '';
        }
      }
      if (this.radiusChipClear) {
        this.radiusChipClear.hidden = !removable;
        this.radiusChipClear.setAttribute('aria-label', this.dataset.labelActiveClear || '');
      }
    }

    if (this.fallbackNotice) {
      this.fallbackNotice.hidden = !showFallback;
      if (showGeolocateError) {
        this.fallbackNotice.textContent = this.geolocateErrorMessage;
      } else if (showGeocodeFallback) {
        this.fallbackNotice.textContent = this.dataset.labelGeocodeFallback || '';
      }
    }

    if (this.radiusEmpty) {
      this.radiusEmpty.hidden = !showEmpty;
      if (showEmpty) {
        if (this.radiusEmptyMessage) {
          this.radiusEmptyMessage.innerHTML = this.#fillTemplate(
            this.dataset.labelEmptyInRadiusHtml,
            { radius: this.filterState.radius, label: this.#displayLabel() }
          );
        }
        this.#renderExpandButtons();
      }
    }
  }

  #renderExpandButtons() {
    if (!this.radiusEmptyActions) return;
    const expandTemplate = this.dataset.labelExpandTo || '';
    const clearOthersLabel = this.dataset.labelClearOthers || '';
    const next = this.fallbackSteps.filter((step) => step > (this.filterState.radius || 0));
    const hasOtherFilters =
      this.filterState.region !== '' ||
      this.filterState.openings.size > 0 ||
      this.filterState.flags.size > 0;

    const expandHtml = next
      .map((step) => {
        const text = expandTemplate
          .replace('{{ radius }}', escapeHtml(String(step)))
          .replace('{radius}', escapeHtml(String(step)));
        return (
          `<button type="button" class="button button-secondary store-locator__radius-expand" ` +
          `data-radius-expand="${step}">${text}</button>`
        );
      })
      .join('');

    const clearHtml =
      hasOtherFilters && clearOthersLabel
        ? `<button type="button" class="button button-secondary store-locator__radius-clear-others" ` +
          `data-radius-clear-others>${escapeHtml(clearOthersLabel)}</button>`
        : '';

    this.radiusEmptyActions.innerHTML = expandHtml + clearHtml;
  }

  #displayLabel() {
    return this.filterState.label || this.filterState.q || '';
  }

  #fillTemplate(template, vars) {
    if (!template) return '';
    let out = template;
    for (const [key, value] of Object.entries(vars)) {
      const safe = escapeHtml(value == null ? '' : String(value));
      out = out.split('{{ ' + key + ' }}').join(safe);
      out = out.split('{' + key + '}').join(safe);
    }
    return out;
  }

  // -- Geocoding -----------------------------------------------------------

  #scheduleGeocode(delay) {
    if (!this.radiusEnabled || !this.apiKey) return;
    if (this.geocodeTimer) clearTimeout(this.geocodeTimer);
    if (this.filterState.q.length < GEOCODE_MIN_QUERY_LENGTH) return;
    if (delay <= 0) {
      void this.#runGeocode();
      return;
    }
    this.geocodeTimer = setTimeout(() => void this.#runGeocode(), delay);
  }

  async #runGeocode() {
    const query = this.filterState.q;
    if (!query) return;

    if (this.geocodeController) this.geocodeController.abort();
    this.geocodeController = new AbortController();
    const signal = this.geocodeController.signal;

    const result = await geocode(query, { apiKey: this.apiKey, signal });
    if (signal.aborted) return;
    // The user may have kept typing while the request was in flight; only
    // accept the result if it still matches the current query.
    if (this.filterState.q !== query) return;

    if (result) {
      this.filterState.lat = result.lat;
      this.filterState.lng = result.lng;
      this.filterState.radius = this.filterState.radius || this.defaultRadius;
      this.filterState.label = result.label || query;
      this.filterState.mode = 'radius';
      this.filterState.userLocation = 'geocode';
      this.geocodeFallbackNoticed = false;
    } else {
      this.filterState.mode = 'text';
      this.filterState.lat = null;
      this.filterState.lng = null;
      this.filterState.radius = null;
      this.filterState.label = '';
      this.filterState.userLocation = null;
      this.geocodeFallbackNoticed = true;
    }
    this.#applyFilters('geocode');
  }

  // -- Multi-page hydration ------------------------------------------------

  async #hydrateRemainingPages() {
    if (!this.list) return;
    const total = parseInt(this.list.dataset.pagesTotal, 10);
    const current = parseInt(this.list.dataset.currentPage, 10);
    if (!total || total <= 1 || total <= current) return;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const pageNumbers = [];
    for (let n = 1; n <= total; n += 1) {
      if (n !== current) pageNumbers.push(n);
    }

    const fragments = [];
    for (let i = 0; i < pageNumbers.length; i += HYDRATION_CONCURRENCY) {
      const batch = pageNumbers.slice(i, i + HYDRATION_CONCURRENCY);
      const batchFragments = await Promise.all(batch.map((page) => this.#fetchPage(page, sectionId)));
      fragments.push(...batchFragments);
    }
    const newCards = fragments
      .flatMap((html) => this.#extractCardsFromHtml(html))
      .filter(Boolean);

    if (newCards.length === 0) return;

    // Safety dedup: drop any fetched card whose handle is already in the DOM.
    const seen = new Set();
    this.list.querySelectorAll('[data-store-card]').forEach((card) => {
      const handle = card.dataset.handle;
      if (handle) seen.add(handle);
    });
    const fragment = document.createDocumentFragment();
    let appended = 0;
    for (const card of newCards) {
      const handle = card.dataset.handle;
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      fragment.appendChild(card);
      appended += 1;
    }
    if (appended === 0) return;
    this.list.appendChild(fragment);

    this.#refreshOpenStatusForAll();
    this.#applyFilters('hydrate');
  }

  async #fetchPage(page, sectionId) {
    // VIS-211: when the locator is injected into the store-selector dialog on a
    // non-locator page, hydrate the remaining pages from the locator's own URL
    // (set via data-hydration-base-url) rather than the host page's URL.
    const base = this.dataset.hydrationBaseUrl || window.location.href;
    const url = new URL(base, window.location.origin);
    const pageParam = (this.list && this.list.dataset.pageParamName) || 'page';
    // Strip any pre-existing page params (the live URL may carry one) so we
    // don't end up with conflicting values.
    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key === 'page' || key.startsWith('page_')) url.searchParams.delete(key);
    });
    url.searchParams.set(pageParam, String(page));
    url.searchParams.set('section_id', sectionId);
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) return '';
      return await response.text();
    } catch {
      return '';
    }
  }

  #extractCardsFromHtml(html) {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('[data-store-card]'));
  }

  // -- Open-now hydration --------------------------------------------------

  #refreshOpenStatusForAll() {
    const now = new Date();
    this.querySelectorAll('[data-store-open-status]').forEach((badge) => {
      this.#refreshOpenStatusFor(badge, now);
    });
  }

  #refreshOpenStatusFor(badge, now) {
    const text = badge.querySelector('.store-card__open-text');
    if (!text) return;

    const json = badge.dataset.openingHoursJson;
    if (!json) {
      badge.dataset.openState = 'unknown';
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch {
      badge.dataset.openState = 'unknown';
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      badge.dataset.openState = 'unknown';
      return;
    }

    const state = openStateAt(parsed, now);
    if (state.open) {
      badge.dataset.openState = 'open';
      text.innerHTML = this.#formatTimeTemplate('templateClosesAt', state.closesAt);
      return;
    }

    badge.dataset.openState = 'closed';
    if (!state.nextOpen) {
      text.textContent = this.dataset.labelClosed || 'Closed';
      return;
    }
    text.innerHTML = this.#formatTimeTemplate('templateOpensAt', state.nextOpen.open);
  }

  #formatTimeTemplate(datasetKey, time) {
    const template = this.dataset[datasetKey];
    if (!template) return escapeHtml(time);
    return template
      .replace('{{ time }}', escapeHtml(time))
      .replace('{time}', escapeHtml(time));
  }

  // -- Geolocate events ----------------------------------------------------

  #handleGeolocated = (event) => {
    const { lat, lng } = event.detail || {};
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    // Drop any in-flight typed-search geocode so its later resolution can't
    // overwrite the visitor's position with whatever they had typed before.
    if (this.geocodeController) this.geocodeController.abort();
    if (this.geocodeTimer) clearTimeout(this.geocodeTimer);
    if (this.searchTimer) clearTimeout(this.searchTimer);

    // The geolocate flow matches the typed-and-geocoded radius search: filter
    // by the section's default radius and sort by distance ascending.
    this.filterState.q = '';
    if (this.searchInput) this.searchInput.value = '';
    this.filterState.lat = lat;
    this.filterState.lng = lng;
    this.filterState.radius = this.defaultRadius;
    this.filterState.label = this.labelCurrentLocation;
    this.filterState.mode = 'radius';
    this.filterState.userLocation = 'geolocate';
    this.geocodeFallbackNoticed = false;
    this.geolocateErrorMessage = '';
    // VIS-283: a re-geolocate from the Mappa tab can land in a 0-results
    // state whose "Expand to N km" actions live in the cards column, so
    // swing back to Lista negozi where the visitor can see + act on them.
    this.setViewMode('list');
    this.#applyFilters('geolocate');
  };

  #handleGeolocateError = (event) => {
    const code = event.detail && event.detail.code;
    const key =
      code === 1
        ? 'labelGeolocateDenied'
        : code === 3
          ? 'labelGeolocateTimeout'
          : 'labelGeolocateUnavailable';
    this.geolocateErrorMessage = this.dataset[key] || '';
    this.#applyFilters('filter');
  };

  // VIS-298: a place picked from the autocomplete dropdown. The element already
  // resolved exact coordinates, so unlike the typed path we skip the geocode
  // entirely and flip straight to radius mode. Mirrors #handleGeolocated but
  // keeps the query text (geolocate clears it) so the chip reads
  // "Entro N km da {label}".
  #handlePlaceSelected = (event) => {
    const detail = event.detail || {};
    const { lat, lng } = detail;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    // Drop any in-flight typed-search geocode so its later resolution can't
    // overwrite the picked place.
    if (this.geocodeController) this.geocodeController.abort();
    if (this.geocodeTimer) clearTimeout(this.geocodeTimer);
    if (this.searchTimer) clearTimeout(this.searchTimer);

    const query = detail.query || '';
    this.filterState.q = query;
    if (this.searchInput) this.searchInput.value = query;
    this.filterState.lat = lat;
    this.filterState.lng = lng;
    this.filterState.radius = this.filterState.radius || this.defaultRadius;
    this.filterState.label = detail.label || query;
    this.filterState.mode = 'radius';
    this.filterState.userLocation = 'geocode';
    this.geocodeFallbackNoticed = false;
    this.geolocateErrorMessage = '';
    // VIS-283: a new location search swings back to Lista negozi so the count +
    // any expand-radius actions are visible.
    this.setViewMode('list');
    this.#applyFilters('place-selected');
  };

  // -- Delegated card clicks -----------------------------------------------

  #handleDelegatedClick = (event) => {
    const copyButton = event.target.closest('[data-store-copy]');
    if (copyButton) {
      event.preventDefault();
      this.#copyToClipboard(copyButton);
    }
  };

  async #copyToClipboard(button) {
    const value = button.dataset.copyValue || '';
    if (!value) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        copyViaTextarea(value);
      }
    } catch {
      copyViaTextarea(value);
    }
    const feedback = button.querySelector('[data-store-copy-feedback]');
    if (feedback) {
      feedback.hidden = false;
      setTimeout(() => {
        feedback.hidden = true;
      }, COPY_FEEDBACK_MS);
    }
  }
}

function copyViaTextarea(value) {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } catch {
    // give up silently
  }
  document.body.removeChild(textarea);
}

// VIS-155: weekend check reads the structured opening_hours JSON serialized
// onto each card's open-status badge (same source as the open-now helper).
// VIS-646: the coming Saturday and Sunday each resolve their own range entry.
function cardOpenWeekend(card) {
  const badge = card.querySelector('[data-store-open-status]');
  const json = badge && badge.dataset.openingHoursJson;
  if (!json) return false;
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== 'object') return false;
  return openOnWeekend(parsed, new Date());
}

function parseCoord(value) {
  if (value == null) return null;
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return null;
  if (num < -180 || num > 180) return null;
  return num;
}

function parseRadius(value) {
  if (value == null) return null;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

function parseFallbackSteps(value) {
  if (typeof value !== 'string' || !value) return [];
  return value
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
}

// VIS-301: is a point inside the map's current bounds? Italy never crosses the
// antimeridian, but handle the west > east wrap defensively for correctness.
function withinBounds(lat, lng, bounds) {
  if (lat < bounds.south || lat > bounds.north) return false;
  if (bounds.west <= bounds.east) return lng >= bounds.west && lng <= bounds.east;
  return lng >= bounds.west || lng <= bounds.east;
}

function haversine(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/`/g, '&#96;');
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
